import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PlusIcon, TrashIcon, PencilSimpleIcon, CoinsIcon, DeviceMobileIcon, WalletIcon } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";

const MODES = [
  { v: "cash", l: "Cash", icon: CoinsIcon },
  { v: "gpay", l: "Google Pay", icon: DeviceMobileIcon },
  { v: "wallet", l: "IOC Paid", icon: WalletIcon },
];
const money = (n) => `₹${(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

export default function Collections() {
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [items, setItems] = useState([]);

  const load = useCallback(async () => {
    try { setItems((await api.get(`/collections?date=${date}`)).data); }
    catch { toast.error("Failed"); }
  }, [date]);
  useEffect(() => { load(); }, [load]);

  const totals = MODES.reduce((acc, m) => {
    acc[m.v] = items.filter((x) => x.mode === m.v).reduce((s, x) => s + x.amount, 0);
    return acc;
  }, {});
  const grand = Object.values(totals).reduce((a, b) => a + b, 0);

  return (
    <div className="space-y-10" data-testid="collections-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 border-b border-slate-200 pb-6">
        <div>
          <div className="overline text-brand-orange-ink">Money in</div>
          <h1 className="font-display text-4xl font-black text-brand-blue">Collections</h1>
          <p className="text-slate-500 mt-2">Enter cash, GPay and wallet in one shot — total is auto-computed per boy / customer.</p>
        </div>
        <div className="flex gap-3 items-end">
          <div>
            <Label className="overline">Date</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="rounded-none w-40" data-testid="collection-date-filter" />
          </div>
          <AddDialog date={date} onDone={load} />
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-0 border border-slate-200 bg-white">
        {MODES.map((m) => (
          <div key={m.v} className="p-6 border-r border-slate-200 last:border-r-0" data-testid={`total-${m.v}`}>
            <div className="flex items-center justify-between">
              <span className="overline text-brand-orange-ink">{m.l}</span>
              <m.icon size={16} className="text-brand-blue" />
            </div>
            <div className="font-mono-num text-2xl font-bold mt-3">{money(totals[m.v])}</div>
          </div>
        ))}
        <div className="p-6 bg-brand-blue text-white" data-testid="total-all">
          <div className="overline text-white/60">Total</div>
          <div className="font-mono-num text-2xl font-bold mt-3">{money(grand)}</div>
        </div>
      </div>

      <div className="border border-slate-200 bg-white overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-brand-blue-tint border-b border-slate-200">
              <TableHead className="uppercase text-xs tracking-wider text-brand-blue">Mode</TableHead>
              <TableHead className="uppercase text-xs tracking-wider text-brand-blue">Delivery Boy</TableHead>
              <TableHead className="uppercase text-xs tracking-wider text-brand-blue">Note</TableHead>
              <TableHead className="text-right uppercase text-xs tracking-wider text-brand-blue">Amount</TableHead>
              {isOwner && <TableHead className="w-20" />}
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((it) => (
              <TableRow key={it.id} className="hover:bg-slate-50 border-b border-slate-100" data-testid={`collection-row-${it.id}`}>
                <TableCell className="font-semibold uppercase text-xs tracking-wider">{it.mode}</TableCell>
                <TableCell className="text-slate-700">{it.delivery_boy || "—"}</TableCell>
                <TableCell className="text-slate-600">{it.note || "—"}</TableCell>
                <TableCell className="text-right font-mono-num font-semibold">{money(it.amount)}</TableCell>
                {isOwner && (
                  <TableCell>
                    <div className="inline-flex gap-2">
                      <EditDialog item={it} onDone={load} />
                      <button
                        onClick={async () => {
                          if (!confirm("Delete this entry?")) return;
                          try { await api.delete(`/collections/${it.id}`); toast.success("Deleted"); load(); }
                          catch { toast.error("Failed"); }
                        }}
                        className="text-slate-400 hover:text-[#FF2400]"
                        data-testid={`delete-collection-${it.id}`}
                      ><TrashIcon size={16} /></button>
                    </div>
                  </TableCell>
                )}
              </TableRow>
            ))}
            {items.length === 0 && (
              <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-10">No collections yet for this date.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      {!isOwner && (
        <p className="text-xs text-slate-500" data-testid="staff-notice">
          Note · you can add new collections but only the owner can edit or delete existing ones.
        </p>
      )}
    </div>
  );
}

function AddDialog({ date, onDone }) {
  const [open, setOpen] = useState(false);
  const [cash, setCash] = useState("");
  const [gpay, setGpay] = useState("");
  const [wallet, setWallet] = useState("");
  const [boy, setBoy] = useState("");
  const [note, setNote] = useState("");
  const [d, setD] = useState(date);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setD(date); }, [date]);

  const total = (parseFloat(cash) || 0) + (parseFloat(gpay) || 0) + (parseFloat(wallet) || 0);

  const submit = async (e) => {
    e.preventDefault();
    const entries = [
      { mode: "cash", amount: parseFloat(cash) || 0 },
      { mode: "gpay", amount: parseFloat(gpay) || 0 },
      { mode: "wallet", amount: parseFloat(wallet) || 0 },
    ].filter((x) => x.amount > 0);
    if (entries.length === 0) return toast.error("Enter at least one amount");
    setSaving(true);
    let ok = 0, fail = 0;
    for (const e of entries) {
      try {
        await api.post("/collections", { ...e, delivery_boy: boy, note, date: d });
        ok += 1;
      } catch (err) {
        fail += 1;
        toast.error(`${e.mode}: ${formatApiError(err.response?.data?.detail)}`);
      }
    }
    setSaving(false);
    if (ok > 0) toast.success(`Recorded ${money(total)} across ${ok} mode(s)${boy ? ` for ${boy}` : ""}`);
    if (fail === 0) {
      setOpen(false); setCash(""); setGpay(""); setWallet(""); setBoy(""); setNote("");
      onDone();
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="rounded-none bg-brand-orange hover:bg-brand-orange-ink text-white" data-testid="add-collection-button">
          <PlusIcon size={16} className="mr-2" /> Add
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded-none border-slate-300 max-w-lg">
        <DialogHeader><DialogTitle className="font-display text-brand-blue">Log collection</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="add-collection-form">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="overline">Delivery boy / customer</Label>
              <Input value={boy} onChange={(e) => setBoy(e.target.value)} className="rounded-none" placeholder="e.g. Ramesh" data-testid="collection-boy-input" />
            </div>
            <div>
              <Label className="overline">Date</Label>
              <Input type="date" value={d} onChange={(e) => setD(e.target.value)} className="rounded-none" data-testid="collection-date-input" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label className="overline flex items-center gap-1"><CoinsIcon size={12} /> Cash</Label>
              <Input type="number" step="0.01" min="0" value={cash} onChange={(e) => setCash(e.target.value)} className="rounded-none font-mono-num" placeholder="0" data-testid="collection-cash-input" />
            </div>
            <div>
              <Label className="overline flex items-center gap-1"><DeviceMobileIcon size={12} /> GPay</Label>
              <Input type="number" step="0.01" min="0" value={gpay} onChange={(e) => setGpay(e.target.value)} className="rounded-none font-mono-num" placeholder="0" data-testid="collection-gpay-input" />
            </div>
            <div>
              <Label className="overline flex items-center gap-1"><WalletIcon size={12} /> Wallet</Label>
              <Input type="number" step="0.01" min="0" value={wallet} onChange={(e) => setWallet(e.target.value)} className="rounded-none font-mono-num" placeholder="0" data-testid="collection-wallet-input" />
            </div>
          </div>
          <div className="border border-brand-blue bg-brand-blue-tint p-3 flex items-center justify-between" data-testid="collection-total-preview">
            <span className="overline text-brand-blue">Total</span>
            <span className="font-mono-num text-2xl font-bold text-brand-blue">{money(total)}</span>
          </div>
          <div>
            <Label className="overline">Note</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} className="rounded-none" data-testid="collection-note-input" />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving || total <= 0} className="rounded-none bg-brand-blue hover:bg-brand-blue-ink text-white disabled:opacity-50" data-testid="collection-submit-button">
              {saving ? "Saving…" : `Save ${money(total)}`}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EditDialog({ item, onDone }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState(item.mode);
  const [amount, setAmount] = useState(String(item.amount || ""));
  const [boy, setBoy] = useState(item.delivery_boy || "");
  const [note, setNote] = useState(item.note || "");
  const [d, setD] = useState(item.date);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.patch(`/collections/${item.id}`, {
        mode, amount: parseFloat(amount), delivery_boy: boy, note, date: d,
      });
      toast.success("Updated");
      setOpen(false); onDone();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="text-slate-400 hover:text-brand-blue" data-testid={`edit-collection-${item.id}`} title="Edit (owner only)">
          <PencilSimpleIcon size={16} />
        </button>
      </DialogTrigger>
      <DialogContent className="rounded-none border-slate-300">
        <DialogHeader><DialogTitle className="font-display text-brand-blue">Edit collection</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="overline">Mode</Label>
              <Select value={mode} onValueChange={setMode}>
                <SelectTrigger className="rounded-none"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MODES.map((m) => <SelectItem key={m.v} value={m.v}>{m.l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="overline">Date</Label>
              <Input type="date" value={d} onChange={(e) => setD(e.target.value)} className="rounded-none" />
            </div>
          </div>
          <div>
            <Label className="overline">Amount (₹)</Label>
            <Input type="number" step="0.01" min="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} required className="rounded-none font-mono-num" />
          </div>
          <div>
            <Label className="overline">Delivery boy / customer</Label>
            <Input value={boy} onChange={(e) => setBoy(e.target.value)} className="rounded-none" />
          </div>
          <div>
            <Label className="overline">Note</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} className="rounded-none" />
          </div>
          <DialogFooter>
            <Button type="submit" className="rounded-none bg-brand-blue hover:bg-brand-blue-ink text-white">Save</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
